// Pure, unit-testable core of the service worker (docs/06-clients.md §4):
// request classification (cache-first shell / network-only API), precache
// bookkeeping, and notification-click routing. sw.ts is a thin event-handler
// shell over these functions so the policy is provable with vitest and the
// browser glue stays minimal.

import { TIMEOUTS } from '@merkury/protocol';
import { inviteDeepLink, parsePushPayload, type PushPayload } from './messages.js';
import { translate, type Lang } from '../i18n/core.js';

// --- Push freshness ---------------------------------------------------------------

/**
 * How long after its server send time (`sentAt`) an `incoming_call` push may
 * still ring. Beyond this it is stale — delayed by a censorship-grade network
 * or replayed from a push-server cache — and ringing would be a ghost call.
 * The window is the server ring timeout plus a generous clock-skew allowance
 * (device clocks drift): we would rather ring a slightly-late real invite than
 * drop it, so we err long. The transport TTLs are the first line of defence;
 * this is the guarantee.
 */
export const CLOCK_SKEW_ALLOWANCE_MS = 75_000;
export const INVITE_STALE_AFTER_MS = TIMEOUTS.RING_MS + CLOCK_SKEW_ALLOWANCE_MS;

/**
 * Whether an incoming-call invite stamped `sentAt` is fresh enough to ring at
 * wall-clock `now` (both epoch millis). Fresh when: no stamp (older server —
 * never drop), the stamp is in the future (clock skew), or its age is within
 * the stale window. Only `incoming_call` is gated; missed/cancelled dismissals
 * are always processed (a late dismissal is harmless, even useful).
 */
export function isFreshInvite(sentAt: number | undefined, now: number): boolean {
  if (sentAt === undefined) return true;
  return now - sentAt <= INVITE_STALE_AFTER_MS;
}

// --- Precache manifest ---------------------------------------------------------

/** Entry shape vite-plugin-pwa injects as `self.__WB_MANIFEST`. */
export type ManifestEntry = string | { url: string; revision: string | null };

export const SHELL_CACHE_PREFIX = 'merkury-shell-';

/** FNV-1a over the manifest: a new deploy ⇒ new cache name ⇒ clean swap. */
export function shellCacheName(manifest: readonly ManifestEntry[]): string {
  const text = JSON.stringify(manifest);
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return `${SHELL_CACHE_PREFIX}${(hash >>> 0).toString(16)}`;
}

/** Absolute same-origin URLs (no revision query needed: cache name rotates). */
export function precacheUrls(manifest: readonly ManifestEntry[], origin: string): string[] {
  const urls = new Set<string>();
  for (const entry of manifest) {
    const url = typeof entry === 'string' ? entry : entry.url;
    urls.add(new URL(url, `${origin}/`).href);
  }
  return [...urls];
}

/** Caches whose name marks them as ours but not the current deploy. */
export function staleCacheNames(existing: readonly string[], current: string): string[] {
  return existing.filter((name) => name.startsWith(SHELL_CACHE_PREFIX) && name !== current);
}

// --- Fetch routing ---------------------------------------------------------------

export type FetchRoute =
  | { kind: 'network-only' } // API/WS/health: never cached, never faked
  | { kind: 'shell-navigation' } // SPA navigation: cached index.html
  | { kind: 'precached'; url: string } // hashed asset: cache-first
  | { kind: 'passthrough' }; // everything else: browser default

// Never cached, never answered from the shell. `/app/` is the APK mirror Caddy
// serves off disk (ci/compose/Caddyfile): typing a download URL in the address
// bar is still `mode: 'navigate'`, so without this entry the shell-navigation
// fallback below would answer /app/merkury-latest.apk with index.html and the
// visitor would land on the main page instead of downloading the build.
const NETWORK_ONLY = [/^\/api\//, /^\/ws$/, /^\/ws\//, /^\/healthz$/, /^\/app\//];

export function classifyFetch(
  request: { url: string; method: string; mode: string },
  origin: string,
  precached: ReadonlySet<string>,
): FetchRoute {
  if (request.method !== 'GET') return { kind: 'network-only' };
  const url = new URL(request.url);
  if (url.origin !== origin) return { kind: 'passthrough' };
  if (NETWORK_ONLY.some((re) => re.test(url.pathname))) return { kind: 'network-only' };
  if (request.mode === 'navigate') return { kind: 'shell-navigation' };
  const bare = `${url.origin}${url.pathname}`;
  if (precached.has(url.href)) return { kind: 'precached', url: url.href };
  if (precached.has(bare)) return { kind: 'precached', url: bare };
  return { kind: 'passthrough' };
}

/** The navigation fallback document, resolved against the precache set. */
export function shellDocumentUrl(origin: string, precached: ReadonlySet<string>): string {
  const index = `${origin}/index.html`;
  if (precached.has(index)) return index;
  return `${origin}/`;
}

// --- Precache -----------------------------------------------------------------

/** The slice of `Cache` the precache needs, so it can be driven in tests. */
export interface PrecacheTarget {
  match(url: string): Promise<unknown>;
  put(url: string, response: Response): Promise<void>;
}

/**
 * Fill the shell cache asset by asset, tolerating individual failures.
 *
 * `cache.addAll` — what this replaces — is all-or-nothing: on a stalling 2G
 * link a single timed-out chunk rejected the whole install, the worker never
 * activated, and the app was left with NO offline shell, failing offline-first
 * on exactly the network class it exists for. Three properties matter:
 *  - resumable: already-cached assets are skipped, so a retried install keeps
 *    the progress an earlier attempt paid for over a slow radio;
 *  - best-effort: a missing chunk is not fatal, because the fetch handler
 *    back-fills it from the network on first use;
 *  - the shell document is the one hard requirement — without it an offline
 *    navigation has nothing to serve, so throwing there is correct: it makes
 *    the browser retry the install on a later visit.
 *
 * Returns the URLs that could not be stored (diagnostic; never thrown for).
 */
export async function precacheShell(
  cache: PrecacheTarget,
  urls: readonly string[],
  shellDoc: string,
  fetchFn: (url: string) => Promise<Response>,
): Promise<string[]> {
  const store = async (url: string): Promise<boolean> => {
    if (await cache.match(url)) return true; // already paid for
    try {
      const response = await fetchFn(url);
      if (!response.ok) return false;
      await cache.put(url, response);
      return true;
    } catch {
      return false; // stalled or aborted — the fetch handler will back-fill
    }
  };

  // The document first: it is the only asset whose absence is unrecoverable.
  if (!(await store(shellDoc))) {
    throw new Error('shell document could not be precached');
  }
  const rest = urls.filter((url) => url !== shellDoc);
  const results = await Promise.all(rest.map(store));
  return rest.filter((_, i) => !results[i]);
}

// --- Push → notification ----------------------------------------------------------

/** A notification action button (Chrome/Edge/Firefox). Safari ignores the
 *  `actions` array and shows a plain notification — see notificationForPush. */
export interface NotificationAction {
  action: 'accept' | 'decline';
  title: string;
}

export interface NotificationSpec {
  title: string;
  options: {
    tag: string;
    data: { callId: string; url: string };
    body: string;
    icon: string;
    requireInteraction: boolean;
    silent: boolean;
    /** Present only for a ring; absent for a low-key/missed notification. */
    actions?: NotificationAction[];
  };
}

/**
 * Platform notification capabilities the pure policy needs. `supportsActions`
 * doubles as the proxy for "this platform has NO silent-push budget": Chrome,
 * Edge and Firefox support action buttons and tolerate a push that shows
 * nothing; Safari/WebKit supports neither — it renders a plain notification and
 * may REVOKE the push subscription if a push shows no notification (docs/06 §4,
 * web/jobs.md §Safari). So on `supportsActions: false` the SW must always show
 * something, even for a stale/cancelled invite. sw.ts detects this once from
 * `Notification.maxActions` and defaults here are the budget-free (Chrome) case
 * so the pure tests need no platform stub.
 */
export interface NotificationCaps {
  supportsActions: boolean;
}

export const DEFAULT_NOTIFICATION_CAPS: NotificationCaps = { supportsActions: true };

/** Low-key, auto-dismissible "the call is no longer ringing" notification shown
 *  ONLY on the Safari silent-push-budget path (a plain close would risk the
 *  subscription). `title`/`body` are generic (no names/keys, push contract). */
function endedNotification(callId: string, lang: Lang): NotificationSpec {
  return {
    title: translate(lang, 'push.endedTitle'),
    options: {
      tag: callId,
      data: { callId, url: '/' },
      body: translate(lang, 'push.endedBody'),
      icon: '/icon-192.png',
      requireInteraction: false,
      silent: true,
    },
  };
}

/** Payloads carry only {type, callId} — the notification text is generic by
 * design (no names or keys through push infrastructure, docs/06 §4). `lang` is
 * resolved by sw.ts from the persisted app-language before this is called
 * (defaults to English so the pure unit tests need no store). */
export function notificationForPush(
  data: unknown,
  lang: Lang = 'en',
  now: number = Date.now(),
  caps: NotificationCaps = DEFAULT_NOTIFICATION_CAPS,
): NotificationSpec | null {
  const payload: PushPayload | null = parsePushPayload(data);
  if (payload === null) return null;
  // A pure cancellation (answered/declined elsewhere) shows nothing — the push
  // handler closes the ringing notification instead (see ringCancelCallId). On
  // a platform with a silent-push budget (Safari) a bare close risks the
  // subscription, so the handler shows a low-key replacement (cancelReplacement).
  if (payload.type === 'call_cancelled') return null;
  const incoming = payload.type === 'incoming_call';
  // Stale invite: we cannot tell a genuinely-missed call from one answered on
  // another device from a delayed invite alone, and the server emits a
  // dedicated missed_call push for real misses — so ringing would be a ghost.
  // Budget-free platforms drop it silently; on Safari (no silent-push budget)
  // showing nothing risks the subscription, so degrade to a low-key notice.
  if (incoming && !isFreshInvite(payload.sentAt, now)) {
    return caps.supportsActions ? null : endedNotification(payload.callId, lang);
  }
  const spec: NotificationSpec = {
    title: translate(lang, incoming ? 'push.incomingTitle' : 'push.missedTitle'),
    options: {
      tag: payload.callId,
      data: {
        callId: payload.callId,
        url: incoming ? inviteDeepLink(payload.callId) : '/',
      },
      body: translate(lang, incoming ? 'push.incomingBody' : 'push.missedBody'),
      icon: '/icon-192.png',
      requireInteraction: incoming, // ring keeps the notification up (45 s hold)
      silent: !incoming,
    },
  };
  // Action buttons on the ring, where supported. Safari ignores the array and
  // shows a plain notification — bare-click still opens the ring (never
  // auto-accepts), so the degradation is designed, not accidental.
  if (incoming && caps.supportsActions) {
    spec.options.actions = [
      { action: 'accept', title: translate(lang, 'push.accept') },
      { action: 'decline', title: translate(lang, 'push.decline') },
    ];
  }
  return spec;
}

/**
 * The low-key replacement to show AFTER closing the ring for a ring-cancelling
 * push, ONLY on a platform with a silent-push budget (Safari). Budget-free
 * platforms (Chrome/Edge/Firefox) close silently and this returns null — no
 * "call ended" spam. Null for any non-cancel payload.
 */
export function cancelReplacement(
  data: unknown,
  lang: Lang = 'en',
  caps: NotificationCaps = DEFAULT_NOTIFICATION_CAPS,
): NotificationSpec | null {
  if (caps.supportsActions) return null;
  const callId = ringCancelCallId(data);
  if (callId === null) return null;
  return endedNotification(callId, lang);
}

/**
 * For a ring-cancelling push (`call_cancelled` — answered/declined on another
 * device), the callId whose ringing notification (tagged by callId) must be
 * closed with no replacement notification; null for any other payload.
 */
export function ringCancelCallId(data: unknown): string | null {
  const payload = parsePushPayload(data);
  return payload !== null && payload.type === 'call_cancelled' ? payload.callId : null;
}

/** Which open window a notification click should focus, if any. */
export function pickClientToFocus<T extends { url: string }>(
  clients: readonly T[],
  origin: string,
): T | null {
  const sameOrigin = clients.filter((c) => c.url.startsWith(origin));
  return sameOrigin[0] ?? null;
}
